import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import Countdown from "@/components/Countdown";
import { useAuth } from "@/context/AuthContext";
import { claimsApi } from "@/lib/api";
import { Sparkles, Flame, Clock, Check, X } from "lucide-react";

export default function MyClaims() {
  const { user, loading } = useAuth();
  const [claims, setClaims] = useState([]);

  useEffect(() => { if (user) claimsApi.mine().then(setClaims); }, [user]);

  if (loading) return <div className="min-h-screen"><NavBar /><div className="text-center py-20">Loading...</div></div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;

  const lvl = user.level || {};
  const pct = Math.round((lvl.progress || 0) * 100);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -100, left: -80 }} />
      <NavBar />
      <div className="max-w-6xl mx-auto px-5 lg:px-10 py-10 relative">
        <span className="sticker sticker-green mb-3 inline-block"><Sparkles size={11} strokeWidth={2.4} /> My Stash</span>
        <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.92] mt-3 mb-7">
          Hey, <span className="text-rainbow">{user.name?.split(" ")[0]}</span>.
        </h1>

        {/* XP CARD */}
        <div className="card-x card-magenta bg-[#0B0F0C] text-[#F5F0DC] p-7 mb-7 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-5xl bouncy">{lvl.icon || "🌱"}</span>
              <div>
                <div className="label-overline text-[#15FF73]">Level {lvl.level}</div>
                <div className="font-display text-4xl font-extrabold">{lvl.name}</div>
              </div>
            </div>
            <div className="h-3 rounded-full bg-[#1B1B22] overflow-hidden border border-[#15FF73]/40">
              <div className="xp-bar h-full" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex justify-between text-xs font-mono-x mt-2 opacity-80">
              <span>{user.xp} XP</span>
              {lvl.next_xp && <span>{lvl.next_xp - user.xp} to {lvl.next_name}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border-2 border-[#15FF73] bg-[#15FF73]/10 p-4 text-center">
              <Flame size={20} className="mx-auto mb-1 text-[#FF7A3D]" />
              <div className="font-display text-3xl font-extrabold text-[#FF7A3D]">{user.streak_days || 0}</div>
              <div className="text-[10px] uppercase tracking-widest font-mono-x font-bold opacity-60">streak</div>
            </div>
            <div className="rounded-2xl border-2 border-[#FF1A8C] bg-[#FF1A8C]/10 p-4 text-center">
              <div className="font-display text-3xl font-extrabold text-[#FF1A8C]">{claims.length}</div>
              <div className="text-[10px] uppercase tracking-widest font-mono-x font-bold opacity-60">claims</div>
            </div>
          </div>
        </div>

        <div className="label-overline text-[#FF1A8C] mb-4">Your claims</div>
        {claims.length === 0 ? (
          <div className="card-x p-12 bg-[#FAF6E8] text-center">
            <div className="text-5xl mb-3">🌫️</div>
            <div className="font-display text-3xl font-extrabold mb-1">No claims yet.</div>
            <Link to="/deals" className="btn-x btn-primary mt-5">Browse deals →</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {claims.map((c) => {
              const tone = c.status === "redeemed" ? "card-green" : c.status === "expired" ? "card-magenta" : "card-cyan";
              return (
                <div key={c.id} className={`card-x ${tone} bg-[#FAF6E8] p-5`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`sticker ${c.status === "redeemed" ? "sticker-green" : c.status === "expired" ? "sticker-magenta" : "sticker-cyan"} !text-[10px]`}>
                      {c.status === "redeemed" ? <><Check size={10} strokeWidth={2.6} /> Redeemed</> : c.status === "expired" ? <><X size={10} strokeWidth={2.6} /> Expired</> : <><Clock size={10} strokeWidth={2.6} /> Active</>}
                    </span>
                    {c.status === "claimed" && <Countdown to={c.expires_at} className="text-xs font-bold" />}
                  </div>
                  <div className="font-mono-x text-sm font-bold tracking-widest bg-[#0B0F0C] text-[#15FF73] rounded-full px-3 py-2 text-center mb-3">{c.code}</div>
                  <div className="font-display text-xl font-extrabold leading-tight mb-1">{c.deal_title}</div>
                  <div className="text-xs text-[#0B0F0C]/55 font-mono-x">${c.deal_price?.toFixed(2)} · -{c.discount_pct}%</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
